// The public feedback page, shared by /feedback/[slug] (a training's current
// run) and /feedback/[slug]/[edition] (one run).
//
// Deliberately its own top-level route rather than a child of
// /training/[slug]: a respondent arrives here from a link handed out at the end
// of a session, and the landing page's sales chrome (sticky CTA, WhatsApp
// float, booking FAB) is exactly wrong around an evaluation form.

import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { LanguageSwitcher } from "#/components/language-switcher";
import { Logo } from "#/components/logo";
import { QuestionnaireForm } from "#/components/questionnaire/public-form";
import { useI18n } from "#/lib/i18n";
import { FORM_STRINGS } from "#/lib/questionnaire";
import type { PublicQuestionnaire } from "#/lib/questionnaire-server";

export function feedbackHead(loaderData: PublicQuestionnaire | undefined) {
	if (!loaderData) return {};
	const title = loaderData.questionnaire?.content.ar.title.trim()
		? `${loaderData.questionnaire.content.ar.title} | ${loaderData.trainingName}`
		: `استمارة تقييم | ${loaderData.trainingName}`;
	return {
		meta: [
			{ title },
			// A feedback form has no business in search results: the link is
			// handed to people who attended, and an indexed form only collects
			// responses from people who never took the training.
			{ name: "robots", content: "noindex, nofollow" },
		],
	};
}

export function FeedbackPage({ data }: { data: PublicQuestionnaire }) {
	const { locale } = useI18n();
	const strings = FORM_STRINGS[locale];
	const copy = data.questionnaire?.content[locale];

	const title = copy?.title.trim() || strings.defaultTitle;

	// Keep the tab title in sync on client-side language switches — head() above
	// only covers the initial SSR render, and it renders the Arabic copy.
	useEffect(() => {
		document.title = `${title} | ${data.trainingName}`;
	}, [title, data.trainingName]);

	return (
		<div className="relative min-h-screen overflow-hidden bg-canvas">
			<Backdrop />

			<div className="relative mx-auto flex min-h-screen max-w-3xl flex-col gap-8 px-4 py-6 sm:px-6 sm:py-10">
				<header className="flex items-center justify-between gap-4">
					<Link to="/" aria-label="أكاديمية مستشير">
						<Logo size="sm" className="sm:h-10" />
					</Link>
					<LanguageSwitcher />
				</header>

				<main className="flex flex-1 flex-col gap-6">
					{data.questionnaire ? (
						<>
							<div className="flex flex-col items-center gap-3 text-center">
								{copy?.eyebrow.trim() ? (
									<span className="inline-flex items-center rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-muted shadow-xs ring-1 ring-line">
										{copy.eyebrow}
									</span>
								) : null}
								<h1 className="font-display text-2xl font-bold leading-tight text-teal-700 sm:text-4xl">
									{title}
								</h1>
								<p className="text-sm font-semibold text-navy/70">
									{data.trainingName}
									{data.editionNumber
										? ` — ${strings.edition} ${data.editionNumber}`
										: ""}
								</p>
								{copy?.description.trim() ? (
									<p className="max-w-xl whitespace-pre-line text-sm leading-relaxed text-muted sm:text-base">
										{copy.description}
									</p>
								) : null}
							</div>

							<QuestionnaireForm
								questionnaireId={data.questionnaire.id}
								questions={data.questionnaire.questions}
								copy={data.questionnaire.content[locale]}
								collectIdentity={data.questionnaire.collectIdentity}
							/>
						</>
					) : (
						<ClosedNotice
							title={strings.closedTitle}
							text={strings.closedText}
							cta={strings.backToTraining}
							slug={data.trainingSlug}
						/>
					)}
				</main>

				<footer className="pb-2 text-center text-xs text-muted">
					© {new Date().getFullYear()} — أكاديمية مستشير
				</footer>
			</div>
		</div>
	);
}

/** Soft teal wash + drifting blobs, matching the printed sheet's background. */
function Backdrop() {
	return (
		<div aria-hidden className="pointer-events-none absolute inset-0">
			<div className="absolute inset-0 bg-gradient-to-b from-teal-50 via-canvas to-canvas" />
			<div className="absolute -start-24 top-24 h-72 w-72 rounded-full bg-teal/10 blur-3xl animate-float-slow" />
			<div className="absolute -end-24 top-1/2 h-80 w-80 rounded-full bg-gold/10 blur-3xl animate-float" />
		</div>
	);
}

function ClosedNotice({
	title,
	text,
	cta,
	slug,
}: {
	title: string;
	text: string;
	cta: string;
	slug: string;
}) {
	return (
		<div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-3xl bg-white px-6 py-14 text-center shadow-card ring-1 ring-line">
			<h1 className="font-display text-2xl font-bold text-navy">{title}</h1>
			<p className="max-w-sm text-sm leading-relaxed text-muted">{text}</p>
			<Link
				to="/training/$slug"
				params={{ slug }}
				className="mt-2 inline-flex items-center rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-teal-600"
			>
				{cta}
			</Link>
		</div>
	);
}
